import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Paper,
  Typography,
} from "@material-ui/core";
import makeStyles from "@material-ui/core/styles/makeStyles";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { ItemState } from "../../redux/state";
import {
  AppBarConfirmButton,
  AppBarSaveButton,
  AppBarButton,
} from "@ofqual/portal-components/components/SecondaryAppBar";
import {
  buildMetaData,
  getLookupTracker,
  modelToJsonPatchDocument,
} from "@ofqual/portal-core/core/utils/modelHelpers";
import {
  ContactTheRegulator,
  CtrStatus,
} from "../../domain/ContactTheRegulator";
import { closeCtr, updatingItem, reOpenCtr } from "../../redux/actions";
import { ModuleAppBar } from "../ModuleAppBar";
import { SectionProps } from "../sections/SectionProps";
import { authSelector } from "../../redux/selectors";
import { RegulatorsToContact } from "../sections/RegulatorsToContact";
import { MessageDetails } from "../sections/MessageDetails";
import { UserType } from "../../redux/types";
import { OfqualDetails } from "../sections/OfqualDetails";
import { Add, NotInterested } from "@material-ui/icons";
import { Prompt } from "react-router-dom";
import { apqService } from "../../services";
import { ExperimentalFeature } from "@ofqual/portal-components/components/ExperimentalFeature";

const useStyles = makeStyles((theme) => ({
  formHeading: {
    color: "#9f9f9f",
    fontSize: "1em",
    marginBottom: "30px",
    fontWeight: 600,
    position: "relative",
  },
  formSection: {
    padding: 30,
    boxShadow: "0 0 19px -2px hsla(0,0%,63.9%,.3)",
    border: "1px solid #ddd",
    backgroundColor: "#fff",
    borderRadius: "3px",
  },
  dialog: {
    minWidth: "500px",
    padding: "20px",
  },
}));

export const ItemDetails: React.FC<{
  state: ItemState;
}> = ({ state }) => {
  const item = state.item!;
  const classes = useStyles();

  const dispatch = useDispatch();
  const { userType, token, experimentalFeatures } = useSelector(authSelector);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [fetching, setFetching] = useState(false);
  const [apqId, setApqId] = useState<string | null>(null);
  const { push } = useHistory();

  useEffect(() => {
    const postApq = async () => {
      const res = await apqService.createApq(item.id, { token });
      console.log(res);
      if (res.status === 409) {
        setApqId(res.data);
        setShowDialog(true);
      } else {
        push(`/assessment-plan-query/${res.data}`);
      }
    };

    if (fetching) {
      postApq();
    }

    return () => setFetching(false);
  }, [fetching, setApqId, setShowDialog, push, setFetching, item.id, token]);

  const isClosed = item.status === CtrStatus.Closed;
  const canEdit = state.status !== "UPDATING";

  const {
    handleSubmit,
    errors,
    control,
    formState,
    setError,
    clearErrors,
    setValue,
    getValues,
  } = useForm<ContactTheRegulator>({
    defaultValues: item,
  });

  // this is important for dirtyFields to be deconstructed/accessed prior to the onSubmit process
  const { dirtyFields } = formState;
  const lookupTracker = useMemo(() => getLookupTracker(), []);

  const onSubmit = (newModel: ContactTheRegulator) => {
    const changedFields = Object.keys(
      dirtyFields
    ) as (keyof ContactTheRegulator)[];
    let document = modelToJsonPatchDocument(newModel, changedFields);
    const resourceTagPatch = document.find(
      (patch) => patch.path === "/resourceTags"
    );
    let resourceTagIds: number[] = item.resourceTags;
    if (resourceTagPatch) {
      resourceTagIds = resourceTagPatch.value;
      document = document.filter((x) => x !== resourceTagPatch);
    }
    const meta = buildMetaData(newModel, item, changedFields, lookupTracker);
    dispatch(updatingItem(item.id, { document, meta }, resourceTagIds));
  };

  const sectionProps: SectionProps<ContactTheRegulator> = {
    user: { userType, token },
    control,
    errors,
    formState,
    setValue: (name, value) => setValue(name, value),
    getValues,
    registerCustomError: (
      name: keyof ContactTheRegulator,
      isError: boolean
    ) => {
      const isCurrentlyARegisteredError = Object.keys(errors).includes(name);
      if (isError && !isCurrentlyARegisteredError) {
        setError(name, {});
      }
      if (!isError && isCurrentlyARegisteredError) {
        clearErrors(name);
      }
    },
    item,
    lookupTracker,
  };
  const props = { ...sectionProps, disabled: true };

  const showClosed =
    userType === UserType.Ofqual ||
    (item.sendToCcea === true &&
      item.sendToOfqual === false &&
      userType === UserType.CCEA)
      ? true
      : false;

  const showReOpen =
    item.status === CtrStatus.Closed &&
    ((userType === UserType.Ofqual && item.sendToOfqual) ||
      (userType === UserType.CCEA && item.sendToCcea && !item.sendToOfqual));

  return (
    <>
      <Prompt
        message="There are unsaved changes in the form.  Are you sure you want to navigate away from this page and lose these changes?"
        when={Object.keys(formState.dirtyFields).length > 0}
      />

      <div>
        <ModuleAppBar breadcrumbs={[{ label: `${item.title}` }]}>
          <ExperimentalFeature
            experimentalModuleName="portal-apq"
            experimentalFeatures={experimentalFeatures}
            experimentalFeatureVisibleName="create-apq-from-ctr"
          >
            {userType === UserType.Ofqual ? (
              <>
                <AppBarConfirmButton
                  tooltip="Create APQ"
                  Icon={Add}
                  onClick={() => setFetching(true)}
                  disabled={!canEdit || isClosed}
                  text="Create APQ"
                  dataTest="create-apq-button"
                >
                  "Are you sure you want to create a new APQ from this CTR"
                </AppBarConfirmButton>
              </>
            ) : null}
          </ExperimentalFeature>
          {showClosed ? (
            <>
              <AppBarConfirmButton
                tooltip={isClosed ? "Re-open" : "Close"}
                Icon={NotInterested}
                onClick={() => {
                  if (isClosed) dispatch(reOpenCtr(item.id));
                  else dispatch(closeCtr(item.id));
                }}
                disabled={!canEdit}
                dataTest={isClosed ? "reopen-ctr" : "close-ctr"}
                text={isClosed ? "Re-open" : "Close"}
              >
                {isClosed
                  ? "Are you sure you want to re-open this request?"
                  : "Are you sure you want to close this request?"}
              </AppBarConfirmButton>
            </>
          ) : null}

          {userType === UserType.Ofqual ? (
            <>
              <AppBarSaveButton
                onClick={handleSubmit(onSubmit)}
                isSubmitting={state.status === "UPDATING"}
                isSubmitted={state.lastCrudResult.isSuccess}
                disabled={!canEdit}
                dataTest="save-ctr"
              />
            </>
          ) : null}
        </ModuleAppBar>

        <form>
          <Typography variant="h6">Regulators</Typography>

          <Paper className={classes.formSection}>
            <RegulatorsToContact {...props} />
          </Paper>

          <Typography variant="h6">Message Details</Typography>

          <Paper className={classes.formSection}>
            <MessageDetails {...props} />
          </Paper>

          {userType === UserType.Ofqual ? (
            <>
              <Typography variant="h6">Ofqual Details</Typography>
              <Paper className={classes.formSection}>
                <OfqualDetails {...sectionProps} />
              </Paper>
            </>
          ) : null}
        </form>
      </div>
      <Dialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        data-test="previously-created-validation"
      >
        <DialogContent className={classes.dialog}>
          An APQ already exists for this CTR
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            onClick={() => setShowDialog(false)}
            data-test="apq-exists-dialog-close-button"
          >
            Close
          </Button>
          <Button
            color="primary"
            onClick={() => push(`/assessment-plan-query/${apqId}`)}
            data-test="navigate-to-apq"
          >
            Go to APQ
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
